/**
 * Sending trading commands and matching their `CommandResult` (SPEC §1.3, §9.9, §9.10; ADR-0021).
 *
 * - **One request per user action, never a retry.** `run` sends exactly once. A send that throws
 *   (socket not open) or a result that never arrives is reported, not repeated: an unanswered
 *   submit may still have reached the exchange (§6.9 UNKNOWN), so the user checks, never HELM.
 * - **Double-click suppression.** Each control has a key. While a key's command awaits its
 *   `CommandResult` the key is busy and further `run`s with it return `null` without sending; the
 *   UI disables the control meanwhile. A 150 ms ⚙ debounce after completion also swallows the
 *   second half of a double tap (§9.10).
 * - **Idempotency.** Callers put a fresh `client_order_id` (`newRequestId`) in every `PlaceOrder`;
 *   the gateway refuses a repeated one for 24 h (`DUPLICATE`).
 * - **No optimistic orders.** Nothing here touches the order book: an order shows as working only
 *   when the gateway's `OrderEvent` says so (trading/state.ts).
 */
import type {
  ManualLockoutResult,
  ManualLockoutInventoryResult,
  ManualLockoutBatchResult,
} from "../gen/helm/v1/risk_pb";
import type { Envelope } from "../gen/helm/v1/envelope_pb";

export type CommandSender = {
  /** Sends a request envelope; returns its req_id. Throws when the socket is not open. */
  send(body: Envelope["body"]): string;
  subscribe(listener: (env: Envelope) => void): () => void;
};

export type CommandOutcome = {
  ok: boolean;
  /** Gateway code (`OK`, `RISK_BLOCKED`, …) or a client code: `NOT_CONNECTED`, `NO_RESPONSE`. */
  code: string;
  /** Shown verbatim (SPEC §9.9). */
  message: string;
  /** Accepted PlaceOrder: the gateway's order id (= `OrderEvent.client_order_id`). */
  orderId: string;
  /** Manual lock application, flatten request and persistence are separate outcomes. */
  lockout?: ManualLockoutResult;
  lockoutInventory?: ManualLockoutInventoryResult;
  lockoutBatch?: ManualLockoutBatchResult;
};

/** ⚙ SPEC §9.10: debounce between identical trading actions. */
export const TRADING_DEBOUNCE_MS = 150;
/** ⚙ How long to wait for a `CommandResult` before telling the user it is unknown. */
export const RESULT_TIMEOUT_MS = 10_000;
/** Batch inventory preflight, account outcomes and final coverage can each take five seconds. */
export const LOCKOUT_BATCH_TIMEOUT_MS = 20_000;

export type TradingCommandsOptions = {
  now?: () => number;
  debounceMs?: number;
  timeoutMs?: number;
  setTimer?: (f: () => void, ms: number) => unknown;
  clearTimer?: (t: unknown) => void;
};

export class TradingCommands {
  private readonly inFlight = new Set<string>();
  private readonly lastDone = new Map<string, number>();
  private readonly pending = new Map<string, { body: Envelope["body"]; done: (o: CommandOutcome) => void }>();
  private readonly listeners = new Set<() => void>();
  private boundTo: CommandSender | null = null;
  private unbind: (() => void) | null = null;
  private readonly now: () => number;
  private readonly debounceMs: number;
  private readonly timeoutMs: number;
  private readonly setTimer: (f: () => void, ms: number) => unknown;
  private readonly clearTimer: (t: unknown) => void;
  private readonly sender: () => CommandSender | null;

  /** Sends sent so far (diagnostics and tests). */
  sent = 0;

  constructor(sender: () => CommandSender | null, opts: TradingCommandsOptions = {}) {
    this.sender = sender;
    this.now = opts.now ?? (() => Date.now());
    this.debounceMs = opts.debounceMs ?? TRADING_DEBOUNCE_MS;
    this.timeoutMs = opts.timeoutMs ?? RESULT_TIMEOUT_MS;
    this.setTimer = opts.setTimer ?? ((f, ms) => setTimeout(f, ms));
    this.clearTimer =
      opts.clearTimer ??
      ((t) => {
        clearTimeout(t as ReturnType<typeof setTimeout>);
      });
  }

  /** Is a command with this key awaiting its result? */
  isBusy(key: string): boolean {
    return this.inFlight.has(key);
  }

  /** Busy keys changed. */
  subscribe(l: () => void): () => void {
    this.listeners.add(l);
    return () => {
      this.listeners.delete(l);
    };
  }

  private emit(): void {
    for (const l of this.listeners) l();
  }

  private bind(s: CommandSender): void {
    if (this.boundTo === s) return;
    this.unbind?.();
    this.boundTo = s;
    this.unbind = s.subscribe((env) => {
      if (
        env.body.case !== "result" &&
        env.body.case !== "manualLockoutResult" &&
        env.body.case !== "manualLockoutInventoryResult" &&
        env.body.case !== "manualLockoutBatchResult"
      )
        return;
      const pending = this.pending.get(env.reqId);
      if (!pending) return;
      const { body, done } = pending;
      if (env.body.case === "manualLockoutInventoryResult") {
        if (body.case !== "manualLockoutInventoryRequest") return;
        done({ ok: true, code: "INVENTORY", message: "", orderId: "", lockoutInventory: env.body.value });
        return;
      }
      if (env.body.case === "manualLockoutBatchResult") {
        const r = env.body.value;
        if (body.case !== "manualLockoutBatch" || r.requestId !== body.value.requestId) return;
        done({
          ok:
            r.code === "COMPLETED" &&
            r.unavailable.length === 0 &&
            body.value.targets.length > 0 &&
            r.results.length === body.value.targets.length &&
            body.value.targets.every((target) => {
              const matches = r.results.filter(
                (item) => item.loginId === target.loginId && item.result?.accountId === target.accountId,
              );
              return matches.length === 1 && matches[0]?.result?.applied === true;
            }),
          code: r.code,
          message: r.message,
          orderId: "",
          lockoutBatch: r,
        });
        return;
      }
      if (env.body.case === "manualLockoutResult") {
        if (body.case !== "manualLockout") return;
        const r = env.body.value;
        done({ ok: r.applied, code: r.code, message: r.message, orderId: "", lockout: r });
        return;
      }
      const r = env.body.value;
      done({ ok: r.ok, code: r.code, message: r.message, orderId: r.orderId });
    });
  }

  /**
   * Send `body` once for control `key`. Resolves with the gateway's answer, a client-side failure
   * (`NOT_CONNECTED`, `NO_RESPONSE`), or `null` when suppressed (the key is busy or was used less
   * than the debounce ago) — in which case nothing was sent.
   */
  async run(key: string, body: Envelope["body"]): Promise<CommandOutcome | null> {
    if (this.inFlight.has(key)) return null;
    const last = this.lastDone.get(key);
    if (
      body.case !== "manualLockoutInventoryRequest" &&
      last !== undefined &&
      this.now() - last < this.debounceMs
    )
      return null;
    const s = this.sender();
    if (s === null) {
      return {
        ok: false,
        code: "NOT_CONNECTED",
        message: "Not connected to the gateway: nothing was sent",
        orderId: "",
      };
    }
    this.bind(s);
    this.inFlight.add(key);
    this.emit();
    try {
      let reqId: string;
      try {
        reqId = s.send(body);
        this.sent++;
      } catch {
        return {
          ok: false,
          code: "NOT_CONNECTED",
          message: "Not connected to the gateway: nothing was sent",
          orderId: "",
        };
      }
      return await new Promise<CommandOutcome>((resolve) => {
        const timeoutMs =
          body.case === "manualLockoutBatch"
            ? Math.max(this.timeoutMs, LOCKOUT_BATCH_TIMEOUT_MS)
            : this.timeoutMs;
        const timer = this.setTimer(() => {
          this.pending.delete(reqId);
          resolve({
            ok: false,
            code: "NO_RESPONSE",
            message:
              body.case === "manualLockoutInventoryRequest"
                ? "No account inventory answer from the gateway. Refresh to check available accounts; no lockout was requested."
                : `No answer from the gateway within ${String(Math.round(timeoutMs / 1000))} s. It may or may not have been applied: check ${body.case === "manualLockout" || body.case === "manualLockoutBatch" ? "each account lockout, Orders and Positions" : "Orders and Positions"}. Nothing was resent.`,
            orderId: "",
          });
        }, timeoutMs);
        this.pending.set(reqId, {
          body,
          done: (o) => {
            this.clearTimer(timer);
            this.pending.delete(reqId);
            resolve(o);
          },
        });
      });
    } finally {
      this.inFlight.delete(key);
      this.lastDone.set(key, this.now());
      this.emit();
    }
  }
}
